import React, {useState, useEffect, useMemo, useCallback } from 'react';
import { lockedLineTarget } from '../../Common/EditOrder/lockedLineTarget';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../Common';
import Hyperlink from '@ajat/design-system/Hyperlink';
import Input from '@ajat/design-system/Input';
import QuantityInput from '@ajat/design-system/QuantityInput';
import {
  HeadingLarge,
  HeadingSmall,
  HeadingMedium,
  StickyFooter,
} from '@ajat/design-system/Configurator';
import InfoTooltip from '@ajat/design-system/InfoTooltip';
import Preloader from '@ajat/design-system/Preloader';
import { usePageLoader } from '@ajat/design-system/PageLoader';
import Gallery from '../../Common/Product/Gallery';
import ClampedText from '../../Common/Product/ClampedText';
import AttributeSelector from '../../Common/Product/AttributeSelector';
import MobileFocusModal from '@ajat/design-system/MobileFocus';
import SizeGuide from '../../Common/Product/SizeGuide';
import { DeliveryBlock } from '@ajat/design-system/SharedBlocks';
import useProductPage from '../../Common/Product/useProductPage';
import ClosedShopMessage from '../../Common/ClosedShop/ClosedShopMessage';
import useProduct from '../../../../hooks/useProduct';
import useVariation from '../../../../hooks/useVariation';
import useSchoolClass from '../../../../hooks/useSchoolClass';
import useSchoolSelection from '../../../../hooks/useSchoolSelection';
import useForceSchoolSelection from '../../../../hooks/useForceSchoolSelection';
import useCompatibilityTree from '../../../../hooks/useCompatibilityTree';
import useControlVisibility, { findUnansweredRevealedSlugs } from '../../../../hooks/useControlVisibility';
import { useCart } from '../../../../context/CartContext';
import SchoolSwitchPopup from '../../Common/SchoolShop/SchoolSwitchPopup';
import { shopSwitchCopyFor } from '../../Common/SchoolShop/schoolSwitchCopy';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import { smoothScrollToTop } from '../../../../utils/scroll';
import { ADDON_TOOLTIPS } from './addonData';
import { SIZE_GUIDE_TITLE, SIZE_GUIDE_ITEMS } from './sizeGuideData';
import productShortDescription from '../../../../utils/shortDescription';
import '../../Common/Product/ProductDetails.css';

// Brand slug used by all ABC-SE storefront API calls.
const BRAND_SLUG = 'abcse';

/* ---------- Brand-specific addon config ---------- */
const ADDON = {
  label: 'Namn',
  price: '+49:-',
  placeholder: 'Skriv namn',
  maxLength: 16,
  helperText: 'Max. 16 tecken',
};

const ProductDetails = () => {
  const { slug, schoolSlug, variationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editOrder = location.state?.editOrder ?? null;
  // Cart-line edit (from the shopping cart): pre-populate like editOrder,
  // but saving replaces the CART line (remove + re-add) instead of an order.
  const editCart = location.state?.editCart ?? null;

  // Variation route: /clothing/abc-se/school-shop/:schoolSlug/product/:variationId
  // Standard PDP:    /clothing/abc-se/product-details/:slug
  const isVariationRoute = Boolean(schoolSlug && variationId);

  // Bare-PDP collection context from listing/collection-shop card links:
  // ?variation={collectionItemId} switches the payload to the collection
  // variation (adapted shape); ?collection={slug} marks the cart line as
  // collection-link origin (checkout then requires school+class).
  const bareSearch = new URLSearchParams(location.search);
  const bareVariationId = !isVariationRoute ? bareSearch.get('variation') : null;
  const collectionSlugParam = !isVariationRoute ? bareSearch.get('collection') : null;

  const { schoolSlug: savedSchoolSlug, className: savedClassName } = useSchoolSelection(BRAND_SLUG);
  // No stored school+class → force the SchoolSelector open (brand-gated).
  // Skipped for order-edit, which carries its own scope.
  const { openShopBlocked } = useForceSchoolSelection({
    brandSlug: BRAND_SLUG,
    hasSelection: Boolean(savedSchoolSlug && savedClassName),
    // Blocking only needs the school - a missing class is not school-less.
    hasSchool: Boolean(savedSchoolSlug),
    // Collection-link PDPs (?collection=) are rep-distributed links that
    // always work — no closed page, no forced selector popup; school + class
    // are collected at checkout instead.
    skip: Boolean(editOrder) || Boolean(editCart) || Boolean(collectionSlugParam),
    // The variation route carries its school scope in the URL — it is not
    // school-less shopping, so never show the closed page there.
    urlScoped: isVariationRoute,
  });
  const productHook   = useProduct(
    isVariationRoute ? null : BRAND_SLUG,
    isVariationRoute ? null : slug,
    savedSchoolSlug,
    savedClassName,
    bareVariationId,
  );
  const savedClass = useSchoolClass(BRAND_SLUG);
  // When editing an order, load the variation with the order's class so the
  // per-class shop overrides (price/attributes/images) match what was bought.
  const className = editOrder?.className ?? savedClass;
  // Shop context for the variation: from the order being edited, else the
  // ?shop query carried from the school-shop card, so overrides resolve to the
  // exact shop the customer is in.
  const shopSlugForVariation = editOrder?.shopSlug ?? new URLSearchParams(location.search).get('shop') ?? null;
  const variationHook = useVariation(isVariationRoute ? BRAND_SLUG : null, schoolSlug, variationId, className, shopSlugForVariation);

  const product = isVariationRoute ? variationHook.data    : productHook.data;
  const loading = isVariationRoute ? variationHook.loading : productHook.loading;
  usePageLoader(loading, { brand: 'ABC', label: 'Laddar…' });
  const error   = isVariationRoute ? variationHook.error   : productHook.error;

  const { addItem, removeItem, setSchool: setCartSchool, school: cartSchool, clearCart } = useCart(BRAND_SLUG);
  /* Deep-link entry: a shared configurator URL is often the customer's FIRST
   * page — only the SchoolShop page binds the cart's school, so such carts
   * checked out school-less (invisible in season/class reports, and the
   * shop's invoice-fee override could not resolve → method default fee).
   * When the cart has NO school yet, bind the variation URL's school; never
   * override an existing binding — the cross-school confirm flow stays on
   * the shop page. Checkout derives the shop (and its fee override) from
   * the school. */
  const variationSchool = variationHook.school;
  useEffect(() => {
    if (!isVariationRoute || !variationSchool?.id) return;
    if (cartSchool?.id != null) return;
    Promise.resolve(setCartSchool({ school_id: variationSchool.id })).catch(() => {});
  }, [isVariationRoute, variationSchool?.id, cartSchool?.id, setCartSchool]);

  const [addingToCart, setAddingToCart] = useState(false);
  // One-shop-per-cart guard: the API refused this add with code
  // 'shop_mismatch' — hold the payload + both shops while the customer
  // decides whether to empty the cart and switch shop.
  const [shopSwitch, setShopSwitch] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);

  /* -- Local state -- */
  const [activeImage, setActiveImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [navnetrykValue, setNavnetrykValue] = useState('');
  const [descExpanded, setDescExpanded] = useState(false);
  // The description is CSS-clamped (2 lines mobile, 3 desktop); only show the read-more link
  // when the text actually overflows the clamp (ClampedText measures it — the
  // clamp ellipsis alone is not clickable, which left desktop stuck).
  const [descClamped, setDescClamped] = useState(false); // set by ClampedText
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [sizeError, setSizeError] = useState('');
  // Flipped on the first blocked add-to-cart with an empty required
  // textfield; each field's inline error clears itself as it is filled.
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  /* -- Pre-populate from editOrder / editCart state -- */
  useEffect(() => {
    const src = editOrder ?? editCart;
    if (!src || !product) return;
    const opts = src.options ?? {};
    // Color: find matching index
    if (opts.color && product.colors) {
      const idx = product.colors.findIndex(
        (c) => c.name?.toLowerCase() === opts.color.toLowerCase()
      );
      if (idx >= 0) setSelectedColor(idx);
    }
    // Size
    if (opts.size) setSelectedSize(opts.size);
    // Quantity
    if (src.quantity) setQuantity(src.quantity);
    // Restore every dynamic attribute selection (incl. priced ones).
    if (opts.attributes && typeof opts.attributes === 'object') {
      setSelectedAttributes(opts.attributes);
    }
    // Addons — namn
    if (opts.addons?.namn) setNavnetrykValue(opts.addons.namn);
  }, [editOrder, editCart, product]);

  /* -- Lock in pinned values + canonical color index for variations -- */
  useEffect(() => {
    if (!product) return;
    // Default the canonical color to the first pinned color value, if any.
    const colorAttr = (product.attributes ?? []).find((a) => a.slug === 'color');
    const firstPinnedId = colorAttr?.pinnedValueIds?.[0];
    if (firstPinnedId != null) {
      const idx = colorAttr.values.findIndex((v) => v.id === firstPinnedId);
      if (idx >= 0) setSelectedColor(idx);
    }
    // Auto-select pinned values for every other attribute.
    setSelectedAttributes((prev) => {
      const next = { ...prev };
      (product.attributes ?? []).forEach((attr) => {
        if (attr.slug === 'color' || attr.slug === 'size') return;
        if (next[attr.slug] != null) return;
        const pinnedId = attr.pinnedValueIds?.[0];
        if (pinnedId != null) next[attr.slug] = pinnedId;
      });
      return next;
    });
  }, [product]);

  /* -- Derived product data -- */
  const images     = product?.images ?? [];
  const colors     = product?.colors ?? [];
  const attributes = product?.attributes ?? [];

  // Canonical color attribute (slug=color) is rendered via the dedicated
  // ColorSelector block; size has its own block too. Everything else is
  // rendered generically by type.
  const sizeAttr      = attributes.find((a) => a.slug === 'size');
  const sizes         = sizeAttr ? sizeAttr.values.map((v) => v.value) : (product?.sizes ?? []);

  // Pinned color value ids → indices in `colors`. Used to lock chips.
  const pinnedColorIdxs = useMemo(() => {
    const ids = product?.pinnedColorValueIds ?? [];
    if (!ids.length) return new Set();
    const colorAttr = attributes.find((a) => a.slug === 'color');
    if (!colorAttr) return new Set();
    const set = new Set();
    colorAttr.values.forEach((val, i) => { if (ids.includes(val.id)) set.add(i); });
    return set;
  }, [product, attributes]);

  /* -- Compatibility tree -- */
  const colorAttr = useMemo(() => attributes.find((a) => a.slug === 'color'), [attributes]);

  const unifiedSelections = useMemo(() => {
    const out = { ...selectedAttributes };
    const colorId = colorAttr?.values?.[selectedColor]?.id;
    const sizeId  = sizeAttr?.values?.find((v) => v.value === selectedSize)?.id;
    if (colorId != null) out.color = colorId;
    if (sizeId  != null) out.size  = sizeId;
    return out;
  }, [selectedAttributes, selectedColor, selectedSize, colorAttr, sizeAttr]);

  const {
    enabled: treeEnabled,
    allowedValueIdsByAttribute,
    soldOutValueIdsByAttribute,
    selectionSoldOut,
    path: attributePath,
    currentNodeImages,
  } = useCompatibilityTree({
    tree:           product?.compatibility_tree ?? [],
    selections:     unifiedSelections,
  });

  // Controls currently hidden (disabled): not rendered, not priced, not saved.
  const hiddenControlSlugs = useControlVisibility({
    hiddenSlugs: product?.hidden_attributes ?? [],
    rules:       product?.visibility_rules ?? [],
    selections:  unifiedSelections,
  });
  const visibleSelectedAttributes = useMemo(() => Object.fromEntries(
    Object.entries(selectedAttributes).filter(([slug]) => !hiddenControlSlugs.has(slug)),
  ), [selectedAttributes, hiddenControlSlugs]);

  // Revealed-by-rule detail dropdowns still unanswered: the customer chose the
  // paid trigger, so the detail is mandatory — add-to-cart blocks on these.
  const unansweredRevealedSlugs = useMemo(() => findUnansweredRevealedSlugs({
    attributes,
    hiddenSlugs: product?.hidden_attributes ?? [],
    hiddenControlSlugs,
    selections: selectedAttributes,
  }), [attributes, product, hiddenControlSlugs, selectedAttributes]);

  const disabledColorIdxs = useMemo(() => {
    const allowed = allowedValueIdsByAttribute.color;
    if (!allowed || !colorAttr) return new Set();
    return new Set(
      colorAttr.values
        .map((v, i) => (allowed.has(v.id) ? null : i))
        .filter((i) => i !== null),
    );
  }, [allowedValueIdsByAttribute, colorAttr]);

  const disabledSizes = useMemo(() => {
    const allowed = allowedValueIdsByAttribute.size;
    if (!allowed || !sizeAttr) return new Set();
    return new Set(
      sizeAttr.values
        .filter((v) => !allowed.has(v.id))
        .map((v) => v.value),
    );
  }, [allowedValueIdsByAttribute, sizeAttr]);

  // Sold-out combos: hidden entirely (not just disabled).
  const soldOutColorIdxs = useMemo(() => {
    const soldOut = soldOutValueIdsByAttribute.color;
    if (!soldOut || !colorAttr) return new Set();
    return new Set(
      colorAttr.values
        .map((v, i) => (soldOut.has(v.id) ? i : null))
        .filter((i) => i !== null),
    );
  }, [soldOutValueIdsByAttribute, colorAttr]);

  const soldOutSizes = useMemo(() => {
    const soldOut = soldOutValueIdsByAttribute.size;
    if (!soldOut || !sizeAttr) return new Set();
    return new Set(
      sizeAttr.values.filter((v) => soldOut.has(v.id)).map((v) => v.value),
    );
  }, [soldOutValueIdsByAttribute, sizeAttr]);

  // When editing an already-paid order, lock any attribute value that carries
  // an additional_price so the customer can swap zero-cost options but cannot
  // change the order total.
  // Paid orders: options are NOT disabled. The footer shows the price to match
  // and blocks "Update" until the total equals the original amount.
  const priceLocked = !!editOrder?.priceLocked;

  const disabledValueIdsByAttribute = useMemo(() => {
    const out = {};
    for (const attr of attributes) {
      if (attr.slug === 'color' || attr.slug === 'size') continue;
      const allowed = allowedValueIdsByAttribute[attr.slug];
      if (!allowed) continue;
      out[attr.slug] = new Set(
        attr.values.filter((v) => !allowed.has(v.id)).map((v) => v.id),
      );
    }
    return out;
  }, [allowedValueIdsByAttribute, attributes]);

  // Clear selections that just became invalid after a sibling change.
  useEffect(() => {
    if (!treeEnabled) return;
    setSelectedAttributes((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const slug of Object.keys(next)) {
        const allowed = allowedValueIdsByAttribute[slug];
        const soldOut = soldOutValueIdsByAttribute[slug];
        if ((allowed && !allowed.has(next[slug])) || (soldOut && soldOut.has(next[slug]))) {
          delete next[slug];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    if (selectedSize && (disabledSizes.has(selectedSize) || soldOutSizes.has(selectedSize))) {
      setSelectedSize(null);
    }
    if (selectedColor != null && soldOutColorIdxs.has(selectedColor)) {
      setSelectedColor(null);
    }
  }, [treeEnabled, allowedValueIdsByAttribute, soldOutValueIdsByAttribute, disabledSizes, soldOutSizes, soldOutColorIdxs, selectedSize, selectedColor]);

  // Per-node images can also come from the variation (CollectionItem) when
  // we're on a school-shop route. Variation overrides win, then tree node
  // images, then plain product images.
  const variationNodeImagesMap = product?.nodeImages ?? null;
  const lastNodeId = attributePath[attributePath.length - 1];
  const variationNodeImages = (variationNodeImagesMap && lastNodeId != null)
    ? (variationNodeImagesMap[lastNodeId] ?? null)
    : null;
  const effectiveImages = (variationNodeImages && variationNodeImages.length > 0)
    ? variationNodeImages
    : ((treeEnabled && currentNodeImages.length > 0) ? currentNodeImages : images);
  // The image currently shown in the gallery - what the customer actually
  // picked (colour/variation overrides included) - is the cart thumbnail.
  const displayedImage = effectiveImages[activeImage] ?? effectiveImages[0];

  const {
    isMobile, focusField, isZoomed, setIsZoomed,
    galleryRef, scrollYBeforeZoom, activeFocusContainerRef,
    handleTouchStart, handleTouchEnd, openFocusMode, closeFocusMode,
  } = useProductPage({
    imageCount: effectiveImages.length,
    activeImage,
    setActiveImage,
    brand: 'ABC',
  });

  const focusFields = {
    navnetryk: { value: navnetrykValue, setValue: setNavnetrykValue, ...ADDON },
  };
  const activeFocusData = focusField ? focusFields[focusField] : focusFields.navnetryk;

  /* -- Price -- */
  const basePrice = product?.priceRaw ?? 0;
  const addonsPrice = navnetrykValue ? 49 : 0;
  // Attributes contribute additional_price two ways:
  //   - textfield: anchor's price applies when the customer typed something
  //   - select/color: the selected value's own additional_price
  const attributesAdditional = useMemo(() => {
    return attributes.reduce((sum, a) => {
      if (hiddenControlSlugs.has(a.slug)) return sum; // hidden control = disabled, not priced
      const sel = selectedAttributes[a.slug];
      if (a.type === 'textfield') {
        if (typeof sel !== 'string' || sel.trim() === '') return sum;
        return sum + (Number(a.values?.[0]?.additional_price) || 0);
      }
      const match = a.values?.find((v) => v.id === sel);
      return sum + (Number(match?.additional_price) || 0);
    }, 0);
  }, [attributes, selectedAttributes, hiddenControlSlugs]);
  const totalPrice = (basePrice + addonsPrice + attributesAdditional) * quantity;

  // Paid orders: keep the same line price; show the target and block Update.
  // Line total to keep: ordered unit price × ORDERED quantity (totalPrice is unit × quantity).
  const targetPrice = lockedLineTarget(editOrder);
  const priceMismatch = priceLocked && targetPrice != null
    && Math.abs(totalPrice - targetPrice) > 0.5;

  /* -- Cart-ready payload -- */
  const cartItem = useMemo(() => ({
    productId: product?.id ?? null,
    slug,
    name: product?.name ?? '',
    color: hiddenControlSlugs.has('color') ? null : (colors[selectedColor] ?? null),
    size: hiddenControlSlugs.has('size') ? null : selectedSize,
    attributes: visibleSelectedAttributes,
    addons: { namn: navnetrykValue || null },
    quantity,
    unitPrice: basePrice,
    totalPrice,
  }), [product, slug, colors, selectedColor, selectedSize, selectedAttributes, navnetrykValue, quantity, basePrice, totalPrice]);

  /* -- Update order handler (edit mode) -- */
  const handleUpdateOrder = useCallback(() => {
    if (sizes.length > 0 && !selectedSize && !hiddenControlSlugs.has('size')) {
      setSizeError('Välj en storlek');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (priceMismatch) return;
    navigate(`/clothing/abc-se/edit-order/${editOrder.orderNumber}`, {
      state: {
        updatedItem: {
          itemId: editOrder.itemId,
          section: editOrder.section,
          options: {
            color: hiddenControlSlugs.has('color') ? null : (colors[selectedColor]?.name || null),
            size: hiddenControlSlugs.has('size') ? null : selectedSize,
            attributes: visibleSelectedAttributes,
            addons: { namn: navnetrykValue || null },
            // Refresh the cart thumbnail to match the chosen colour/variant.
            image: displayedImage?.src || displayedImage?.url || null,
          },
          quantity,
        },
      },
    });
  }, [selectedSize, priceMismatch, navigate, editOrder, colors, selectedColor, selectedAttributes, navnetrykValue, quantity, displayedImage]);

  /* -- Update cart line handler (cart-edit mode) --
   * The BE merges identical lines on add, so the old line is removed FIRST
   * (add-first would merge into it and the removal would wipe both); if the
   * re-add fails, the original line is restored. Server re-prices. */
  const handleUpdateCartItem = useCallback(async () => {
    if (sizes.length > 0 && !selectedSize && !hiddenControlSlugs.has('size')) {
      setSizeError('Välj en storlek');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      const image = displayedImage?.src || displayedImage?.url || null;
      const payload = {
        itemType: 'product',
        productId: cartItem.productId,
        quantity: cartItem.quantity,
        unitPrice: cartItem.unitPrice,
        additionalPrice: attributesAdditional,
        options: {
          name: cartItem.name,
          slug: cartItem.slug || editCart?.options?.slug || null,
          image,
          color: cartItem.color?.name || null,
          size: cartItem.size,
          attributes: cartItem.attributes,
          addons: cartItem.addons,
          ...(treeEnabled && attributePath.length > 0
            ? { attribute_path: attributePath }
            : {}),
          ...(product?.variationId
            ? {
                collectionItemId: product.variationId,
                ...(isVariationRoute ? { schoolSlug } : {}),
                ...(collectionSlugParam ? { collectionSlug: collectionSlugParam } : {}),
              }
            : {}),
        },
      };
      await removeItem(editCart.itemId);
      try {
        await addItem(payload);
      } catch (err) {
        // Best-effort restore of the original line so it isn't lost.
        try {
          await addItem({
            itemType: 'product',
            productId: cartItem.productId,
            quantity: editCart.quantity || 1,
            options: editCart.options || {},
          });
        } catch { /* restore is best-effort */ }
        throw err;
      }
      navigate('/clothing/abc-se/shopping-cart');
    } catch (err) {
      console.error('Cart item update failed', err);
    } finally {
      setAddingToCart(false);
    }
  }, [selectedSize, addingToCart, displayedImage, cartItem, addItem, removeItem, editCart, treeEnabled, attributePath, isVariationRoute, product, schoolSlug, attributesAdditional, navigate, hiddenControlSlugs]);

  /* -- Add to cart handler -- */
  const handleAddToCart = useCallback(async () => {
    if (sizes.length > 0 && !selectedSize && !hiddenControlSlugs.has('size')) {
      setSizeError('Välj en storlek');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    // Required textfields (attr.is_required, e.g. Vogntrøjer's Navnetryk /
    // Nummertryk): block add-to-cart while any is empty — flips on the
    // per-field inline errors and scrolls to the first offender. Mirrors the
    // BE's 422 refusal in addItem.
    const missingRequired = (attributes || []).filter((a) =>
      a.type === 'textfield' && a.is_required && !hiddenControlSlugs.has(a.slug)
      && !(typeof selectedAttributes[a.slug] === 'string' && selectedAttributes[a.slug].trim()));
    if (missingRequired.length > 0 || unansweredRevealedSlugs.length > 0) {
      setShowRequiredErrors(true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('[data-required-empty]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (addingToCart) return;
    setAddingToCart(true);
    // Declared outside the try so the catch can hand the same payload to the
    // shop-switch popup for a retry.
    let addPayload = null;
    try {
      const image = displayedImage?.src || displayedImage?.url || null;
      addPayload = {
        itemType: 'product',
        productId: cartItem.productId,
        quantity: cartItem.quantity,
        unitPrice: cartItem.unitPrice,
        additionalPrice: attributesAdditional,
        options: {
          name: cartItem.name,
          slug: cartItem.slug,
          image,
          color: cartItem.color?.name || null,
          size: cartItem.size,
          attributes: cartItem.attributes,
          addons: cartItem.addons,
          ...(treeEnabled && attributePath.length > 0
            ? { attribute_path: attributePath }
            : {}),
          // When this PDP was reached via the school-shop route, persist the
          // collection/variation id so the BE can reconcile the line item.
          ...(product?.variationId
            ? {
                collectionItemId: product.variationId,
                ...(isVariationRoute ? { schoolSlug } : {}),
                ...(collectionSlugParam ? { collectionSlug: collectionSlugParam } : {}),
              }
            : {}),
        },
      };
      await addItem(addPayload);
      // Success: brief checkmark on the button + a 1s ease-in-out scroll to top.
      setAddSuccess(true);
      smoothScrollToTop(1000);
      setTimeout(() => setAddSuccess(false), 2000);
      document.dispatchEvent(new CustomEvent('open-minicart'));
    } catch (err) {
      if (err?.data?.code === 'shop_mismatch') {
        setShopSwitch({ payload: addPayload, cartShop: err.data.cartShop, lineShop: err.data.lineShop });
      } else {
        console.error('Add to cart failed', err);
        pushNotification({ type: 'error', title: 'Kunde inte lägga i varukorgen', message: err?.data?.error || err?.message || '' });
      }
    } finally {
      setAddingToCart(false);
    }
  }, [selectedSize, addingToCart, displayedImage, cartItem, addItem, treeEnabled, attributePath, isVariationRoute, product, schoolSlug, attributesAdditional]);

  /* One-shop-per-cart: the customer confirmed emptying the cart to order
   * from the shop this product belongs to. Empty → bind that shop (school
   * and class unchanged) → re-issue the exact add that was refused. */
  const confirmShopSwitch = useCallback(async () => {
    if (!shopSwitch) return;
    const { payload, lineShop } = shopSwitch;
    setShopSwitch(null);
    setAddingToCart(true);
    try {
      await clearCart();
      if (cartSchool?.id && lineShop?.id) {
        await setCartSchool({ school_id: cartSchool.id, shop_id: lineShop.id, school_class: cartSchool.class ?? null });
      }
      await addItem(payload);
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 2000);
      document.dispatchEvent(new CustomEvent('open-minicart'));
    } catch (err) {
      console.error('Shop switch failed', err);
      pushNotification({ type: 'error', title: 'Kunde inte lägga i varukorgen', message: err?.data?.error || err?.message || '' });
    } finally {
      setAddingToCart(false);
    }
  }, [shopSwitch, clearCart, setCartSchool, cartSchool?.id, cartSchool?.class, addItem]);

  /* Open shop disabled + no school on a selector-less brand → closed page. */
  if (openShopBlocked) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} /></div>
        <ClosedShopMessage brandSlug={BRAND_SLUG} feSlug="abc-se" standalone />
      </div>
    );
  }

  /* -- Loading / error states -- */
  if (loading) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Preloader />
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <p>Product not found.</p>
        </main>
      </div>
    );
  }

  return (
    <div className={`pp ${isZoomed ? 'pp--zoomed' : ''}`}>
      {/* ═══ HEADER ═══ */}
      <div className="pp__header">
        <Header hideOnScroll={false} />
      </div>
      <div className="pp__header-mobile">
        <Header hideOnScroll={false} />
      </div>

      {/* ═══ MAIN ═══ */}
      <main className="pp__main">
        <Gallery
          alt={product?.name}
          images={effectiveImages}
          activeImage={activeImage}
          setActiveImage={setActiveImage}
          isZoomed={isZoomed}
          setIsZoomed={setIsZoomed}
          scrollYBeforeZoom={scrollYBeforeZoom}
          galleryRef={galleryRef}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={handleTouchEnd}
        />

        <div className="pp__configurator">
          <HeadingLarge
            titleAs="h1"
            subtitle={productShortDescription(product)}
            title={product.name}
            price={product.price}
            description={
              <>
                <ClampedText text={product.description} expanded={descExpanded} onOverflowChange={setDescClamped} />
                {!descExpanded && descClamped && (
                  <Hyperlink
                    variant="small"
                    className="pdp__read-more"
                    onClick={(e) => { e.preventDefault(); setDescExpanded(true); }}
                    href="#"
                  >
                    Läs mer
                  </Hyperlink>
                )}
              </>
            }
          />

          <div className="pp__options">
            <AttributeSelector
              colors={colors}
              sizes={sizes}
              attributes={attributes}
              selectedColor={selectedColor}
              selectedSize={selectedSize}
              selectedAttributes={selectedAttributes}
              onColorChange={setSelectedColor}
              onSizeChange={(s) => { setSelectedSize(s); setSizeError(''); }}
              onAttributeChange={(slug, valueId) =>
                setSelectedAttributes((prev) => ({ ...prev, [slug]: valueId }))
              }
              pinnedColorIdxs={pinnedColorIdxs}
              sizeError={sizeError}
              labels={{ color: 'Färg', size: 'Storlek', sizeGuide: 'Storleksguide', textfieldError: 'Texten är för lång eller innehåller ogiltiga tecken', requiredError: 'Fyll i detta fält' }}
              showRequiredErrors={showRequiredErrors}
              requiredEmptySlugs={new Set(unansweredRevealedSlugs)}
              onSizeGuideOpen={() => setIsSizeGuideOpen(true)}
              showSizeGuide={Boolean(product?.sizeguide_override)} /* ABC-SE: only per-product override content, like CLS-SE */
              disabledColorIdxs={disabledColorIdxs}
              disabledSizes={disabledSizes}
              disabledValueIdsByAttribute={disabledValueIdsByAttribute}
              soldOutColorIdxs={soldOutColorIdxs}
              soldOutSizes={soldOutSizes}
              soldOutValueIdsByAttribute={soldOutValueIdsByAttribute}
              hiddenControlSlugs={hiddenControlSlugs}
            />

            {/* ── Quantity + Customise (visible after size selected) ── */}
            {selectedSize && (
              <>
                {/* ── Quantity ── */}
                <div className="pp__section">
                  <HeadingSmall title="Antal" showSelection={false} />
                  <QuantityInput
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                    max={99}
                  />
                </div>

                {/* ── Customise (Name only) ── */}
                {/* <div className="pp__addons-reveal">
              <HeadingMedium title="Anpassa" />
              <div className="pp__section">
                <HeadingSmall
                  title={ADDON.label}
                  infoTooltip="Klicka för mer info"
                  onInfoClick={() => setActiveTooltip('navnetryk')}
                  showSelection={false}
                  price={ADDON.price}
                />
                {isMobile ? (
                  <div
                    id="mf-trigger-navnetryk"
                    className="mf__trigger"
                    onClick={(e) => { e.preventDefault(); openFocusMode('navnetryk'); }}
                  >
                    <Input
                      type="text"
                      showIcon={false}
                      placeholder={ADDON.placeholder}
                      label={ADDON.placeholder}
                      value={navnetrykValue}
                      readOnly
                      tabIndex={-1}
                      onChange={(e) => setNavnetrykValue(e.target.value)}
                      maxLength={ADDON.maxLength}
                      helperText={ADDON.helperText}
                    />
                  </div>
                ) : (
                  <Input
                    type="text"
                    showIcon={false}
                    placeholder={ADDON.placeholder}
                    label={ADDON.placeholder}
                    value={navnetrykValue}
                    onChange={(e) => setNavnetrykValue(e.target.value)}
                    maxLength={ADDON.maxLength}
                    helperText={ADDON.helperText}
                  />
                )}
              </div>
            </div> */}
              </>
            )}
          </div>

          {/* Delivery info hidden for ABC for now. */}
          {false && !editOrder && <DeliveryBlock time="3 Veckor" cost="Gratis" description="Leverans till din skola" />}
        </div>
      </main>

      {/* ═══ STICKY FOOTER ═══ */}
      <StickyFooter
        buttonSuccess={addSuccess}
        price={`${totalPrice}:-`}
        priceError={priceMismatch}
        targetText={priceLocked && targetPrice != null ? `Måste matcha ${targetPrice}:-` : null}
        showDelivery={false}
        deliveryMessage={product?.deliveryInformation || null}
        onButtonClick={editOrder ? handleUpdateOrder : editCart ? handleUpdateCartItem : handleAddToCart}
        buttonLabel={selectionSoldOut ? 'Slutsåld' : ((editOrder || editCart) ? 'Uppdatera' : 'Lägg i varukorg')}
        buttonDisabled={priceMismatch || selectionSoldOut}
        className={`pp__footer ${focusField ? 'pp__footer--hidden' : ''}`}
      />

      {/* ═══ SIZE GUIDE ═══ */}
      <SchoolSwitchPopup
        isOpen={!!shopSwitch}
        brandSlug="abcse"
        copy={shopSwitchCopyFor('abcse', { from: shopSwitch?.cartShop?.name, to: shopSwitch?.lineShop?.name })}
        onBack={() => setShopSwitch(null)}
        onConfirm={confirmShopSwitch}
      />
      <SizeGuide isOpen={isSizeGuideOpen} onClose={() => setIsSizeGuideOpen(false)} title={SIZE_GUIDE_TITLE} items={SIZE_GUIDE_ITEMS} overrideHtml={product?.sizeguide_override || null} />

      {/* ═══ ADDON TOOLTIP ═══ */}
      <InfoTooltip
        isOpen={!!activeTooltip}
        onClose={() => setActiveTooltip(null)}
        ariaLabel={activeTooltip ? ADDON_TOOLTIPS[activeTooltip].title : ''}
      >
        {activeTooltip && (
          <>
            <h3 className="pp-addon-tip__title typo-h3">
              {ADDON_TOOLTIPS[activeTooltip].title}
            </h3>
            {ADDON_TOOLTIPS[activeTooltip].image && (
              <div className="pp-addon-tip__image-wrap">
                <img
                  src={ADDON_TOOLTIPS[activeTooltip].image}
                  alt={ADDON_TOOLTIPS[activeTooltip].title}
                  className="pp-addon-tip__image"
                />
              </div>
            )}
            {ADDON_TOOLTIPS[activeTooltip].description && (
              <div className="pp-addon-tip__description typo-body">
                {ADDON_TOOLTIPS[activeTooltip].description.split('\n\n').map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
          </>
        )}
      </InfoTooltip>

      {/* ═══ MOBILE FOCUS MODAL ═══ */}
      {isMobile && (
        <MobileFocusModal
          focusField={focusField}
          activeFocusData={activeFocusData}
          closeFocusMode={closeFocusMode}
          containerRef={activeFocusContainerRef}
        />
      )}
    </div>
  );
};

export default ProductDetails;
